/**
 * Is the non-dismissible email-verification gate about to own the screen?
 *
 * WHY THIS EXISTS. `components/auth/email-verification-gate-modal.tsx` is
 * mounted globally (`app/layout.tsx`) and portals to `document.body` as
 * `fixed inset-0 z-[9999]`, with no ✕ and no backdrop close. The onboarding
 * wizard renders at `z-[100]` INSIDE the page, whose root is `position:
 * relative; z-index: auto` — which creates no stacking context — so the gate
 * paints straight over the wizard and swallows every click.
 *
 * On its own that was merely invisible. It became destructive when the wizard
 * started persisting "shown" the moment it renders (migration 166): an
 * email+password signup would stamp `onboarding_shown_at` underneath a modal
 * they cannot see, the gate would `window.location.reload()` on verification,
 * and the wizard would then be suppressed forever — on every surface and every
 * device. The user would see Quick setup exactly zero times.
 *
 * So: defer. Do not render and do not stamp while verification is owed. The
 * post-verification reload re-runs the whole check and the wizard shows then.
 *
 * Fails CLOSED (treats the gate as up when it cannot tell). Deferring costs one
 * page load; stamping under the gate costs the impression permanently.
 */
export async function isEmailVerificationGatePending(): Promise<boolean> {
  if (typeof window === "undefined") return true;
  try {
    const res = await fetch("/api/auth/verification-status", {
      cache: "no-store",
    });
    if (!res.ok) return true;
    const body = (await res.json()) as { must_verify?: boolean } | null;
    return Boolean(body?.must_verify);
  } catch {
    return true;
  }
}
